import moment from "moment";

export class DateHelper {
  public static durationDays(date: Date, date2: Date): number {
    const timeDifference = Math.abs(date2.getTime() - date.getTime());
    return Math.ceil(timeDifference / (1000 * 60 * 60 * 24));
  }

  public static convertToTimeZone(date: Date, timeZone: string): string {
    if (!date || !timeZone)
      return '';

    // Create a formatter for the specific time zone
    const formatter = new Intl.DateTimeFormat('en-gb', {
      timeZone: timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });

    // Format the given UTC date into the specified time zone
    return formatter.format(date);
  }

  public static toDateNoTimeZone(dateStr: string): Date | null {
    if (!dateStr) return null;

    const momentDate = moment.utc(dateStr);
    const localDate = new Date(momentDate.year(), momentDate.month(), momentDate.date(), momentDate.hour(), momentDate.minute(), momentDate.second());

    return localDate;
  }

  public static addDaysToDate(date: Date | string, days: number): Date {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
  }

  public static dateAsUTC(date: Date) {
    let userTimezoneOffset = date.getTimezoneOffset() * 60000;

    return new Date(date.getTime() - userTimezoneOffset);
  }

  public static timeToMinutes(hrs: number, minutes: number, period: string) {
    if (hrs > 0) {
      if (period === 'PM' && hrs < 12) {
        hrs = hrs + 12;
      }

      return (hrs * 60) + minutes;
    }

    return null;
  }

  public static convert24HourTimeToMinutes(time: string): number {
    // Split the time string into hours and minutes
    const [hours, minutes] = time.split(":").map(Number);

    // Calculate total minutes since midnight
    const totalMinutes = (hours * 60) + minutes;

    return totalMinutes;
  }

  public static convertMinutesTo24HourTime(minutes: number): string {
    // Calculate hours and minutes
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;

    // Pad with leading zeros if necessary and format the time
    const hoursFormatted = String(hours).padStart(2, '0');
    const minutesFormatted = String(mins).padStart(2, '0');

    return `${hoursFormatted}:${minutesFormatted}`;
  }

  public static minutesToTimeStr(totalMinutes: number) {
    let period = 'AM';
    let lz = s => ('00' + s).slice(-2);
    // @ts-ignore
    let hours = Math.round(Math.floor(totalMinutes / 60));
    // @ts-ignore
    let mins = totalMinutes - (hours * 60);

    if (hours >= 12) {
      period = 'PM';
    }

    if (hours > 12) {
      hours = hours - 12;
    }

    return hours + ':' + lz(mins) + " " + period;
  }

  public static dateUTCToLocal(sourceDate: Date) {
    if (sourceDate === null) {
      return null;
    }

    return new Date(sourceDate + 'Z');
  }

  public static dateToInputStr(date: Date) {
    let lz = s => ('00' + s).slice(-2);
    let month: number = date.getMonth() + 1;
    return lz(date.getDate()) + '/' + lz(month) + '/' + date.getFullYear();
  }

  public static dateToInputStrV2(date: Date | string): string {
    if (!date) return '';

    // Convert to a Date object if it's a string
    if (typeof date === 'string') {
      date = new Date(date);
    }

    // Handle invalid date strings
    if (isNaN(date.getTime())) {
      return '';
    }

    return (`00${date.getDate()}`).slice(-2) + '/' + (`00${date.getMonth() + 1}`).slice(-2) + '/' + date.getFullYear();
  }


  public static dateToDdMmYyyy(date: Date) {
    if (date === undefined || date == null || date instanceof Date === false)
      return '';

    let lz = s => ('00' + s).slice(-2);
    let month: number = date.getMonth() + 1;
    return lz(date.getDate()) + '/' + lz(month) + '/' + date.getFullYear();
  }

  public static dateToYyyyMmDd(date: Date) {
    if (date == null)
      return '';

    // Convert to a Date object if it's a string
    if (typeof date === 'string') {
      date = new Date(date);
    }

    let lz = s => ('00' + s).slice(-2);
    let month: number = date.getMonth() + 1;
    return date.getFullYear() + '/' + lz(month) + '/' + lz(date.getDate());
  }

  public static yyyyMmDdToDate(str: string) {
    if (str == null || str === '') {
      return null;
    }

    return new Date(str.replace(new RegExp("/", "g"), "-") + 'T12:00:00.000Z');
  }

  public static stringToDate(str: string) {
    if (str === undefined || str == null || str === '') {
      return null;
    }

    const parts = str.split('/');

    return new Date(parts[2] + '-' + parts[1] + '-' + parts[0] + 'T12:00:00.000Z');
  }

}

export class IllyTime {
  public hours: number = 0;
  public minutes: number = 0;
  public period: string = 'AM';
  public isValid = false;

  public static parseMinutes(totalMinutes: number): IllyTime {
    let t = new IllyTime();
    if (totalMinutes >= 0) {
      t.hours = Math.floor(totalMinutes / 60);
      t.minutes = totalMinutes % 60;
      t.period = t.hours >= 12 ? 'PM' : 'AM';
      t.isValid = true;

      if (t.hours > 12) {
        t.hours = t.hours - 12;
      }

      return t;
    }

    return t;
  }

  public static parseString(time: string): IllyTime {
    let t = new IllyTime();
    if (time !== null && time.length === 5 && time.includes(':')) {
      let parts = time.split(':');
      t.hours = +parts[0];
      t.minutes = +parts[1];
      t.period = t.hours >= 12 ? 'PM' : 'AM';

      if (t.hours > 12) {
        t.hours = t.hours - 12;
      }

      t.isValid = true;

      return t;
    }

    return t;
  }

  hoursAs24(): number {
    return this.period === 'PM' && this.hours < 12 ? this.hours + 12 : this.hours;
  }

  /**
   * Returns time as 24 hour string i.e. 23:30
   */
  to24HourString(): string {
    if (this.isValid) {
      let lz = s => ('00' + s).slice(-2);
      let hours = this.hoursAs24();

      return lz(hours) + ':' + lz(this.minutes);
    }

    return '';
  }

  /**
   * Returns the time expressed in minutes i.e. 630 (10:30)
   */
  totalMinutes(): number {
    if (this.isValid) {
      if (this.period === 'PM') {
        return ((this.hours % 12 + 12) * 60) + this.minutes;
      } else {
        return (this.hours * 60) + this.minutes;
      }
    }

    return null;
  }
}
